// =============================================================================
// deliverablesUpload.ts — shared upload helpers for the project deliverables
// library (T-067). All bytes flow through the api-gateway Worker's
// /deliverables/* endpoints into Cloudflare R2.
//
// Two upload paths:
//   * uploadProjectDeliverable — anyone on the project team uploads a filled
//     version against a deliverables line (Red Line Markup, As Built SID,
//     etc.).
//   * uploadMasterTemplate — manager+ uploads/replaces the blank template
//     for a line (the "what good looks like" reference). Re-uploading
//     archives the prior master into history.
//
// Read paths use /deliverables/sign-download with RLS-aware access checks
// (masters readable by all auth, uploads scoped to project team).
// =============================================================================

import { apiFetch } from "@/lib/supabase";

export interface ProjectDeliverableUploadArgs {
  projectId:           string;
  lineId:              string;
  file:                File;
  versionLabel?:       string | null;
  notes?:              string | null;
  status?:             "draft" | "submitted" | "accepted";
  supersedesUploadId?: string | null;
  onProgress?:         (phase: "upload" | "save", pct: number) => void;
}

export interface MasterTemplateUploadArgs {
  lineId:        string;
  file:          File;
  versionLabel?: string | null;
  archiveNotes?: string | null;
  onProgress?:   (phase: "upload" | "save", pct: number) => void;
}

export interface DeliverableUploadResult {
  upload_id:    string;
  storage_path: string;
}

export interface MasterUploadResult {
  line_id:    string;
  object_key: string;
}

// -----------------------------------------------------------------------------
export async function uploadProjectDeliverable(
  args: ProjectDeliverableUploadArgs,
): Promise<DeliverableUploadResult> {
  const { projectId, lineId, file, onProgress } = args;
  const finalMime = file.type || "application/octet-stream";

  // 1. Sign URL
  const signRes = await apiFetch("/deliverables/sign-upload", {
    method: "POST",
    body: JSON.stringify({
      kind:         "upload",
      project_id:   projectId,
      line_id:      lineId,
      file_name:    file.name,
      content_type: finalMime,
    }),
  });
  if (!signRes.ok) {
    const err = await signRes.json().catch(() => ({}));
    throw new Error(`sign-upload failed (${signRes.status}): ${err.error ?? "unknown"}`);
  }
  const { upload_url, object_key } = await signRes.json();

  // 2. PUT bytes
  onProgress?.("upload", 0);
  const putRes = await fetch(upload_url, {
    method:  "PUT",
    headers: { "Content-Type": finalMime },
    body:    file,
  });
  if (!putRes.ok) {
    throw new Error(`R2 upload failed: ${putRes.status} ${putRes.statusText}`);
  }
  onProgress?.("upload", 100);

  // 3. Finalise
  onProgress?.("save", 0);
  const finRes = await apiFetch("/deliverables/finalise-upload", {
    method: "POST",
    body: JSON.stringify({
      project_id:           projectId,
      line_id:              lineId,
      object_key,
      file_name:            file.name,
      file_size_bytes:      file.size,
      mime_type:            finalMime,
      version_label:        args.versionLabel ?? null,
      notes:                args.notes ?? null,
      status:               args.status ?? "submitted",
      supersedes_upload_id: args.supersedesUploadId ?? null,
    }),
  });
  if (!finRes.ok) {
    const err = await finRes.json().catch(() => ({}));
    throw new Error(`finalise-upload failed (${finRes.status}): ${err.error ?? "unknown"}${err.detail ? ` — ${err.detail}` : ""}`);
  }
  const { upload_id, storage_path } = await finRes.json();
  onProgress?.("save", 100);

  return { upload_id, storage_path };
}

// -----------------------------------------------------------------------------
export async function uploadMasterTemplate(
  args: MasterTemplateUploadArgs,
): Promise<MasterUploadResult> {
  const { lineId, file, onProgress } = args;
  const finalMime = file.type || "application/octet-stream";

  // 1. Sign URL (kind='master', mgr+ only — server enforces)
  const signRes = await apiFetch("/deliverables/sign-upload", {
    method: "POST",
    body: JSON.stringify({
      kind:         "master",
      line_id:      lineId,
      file_name:    file.name,
      content_type: finalMime,
    }),
  });
  if (!signRes.ok) {
    const err = await signRes.json().catch(() => ({}));
    throw new Error(`sign-upload failed (${signRes.status}): ${err.error ?? "unknown"}`);
  }
  const { upload_url, object_key } = await signRes.json();

  // 2. PUT bytes
  onProgress?.("upload", 0);
  const putRes = await fetch(upload_url, {
    method:  "PUT",
    headers: { "Content-Type": finalMime },
    body:    file,
  });
  if (!putRes.ok) {
    throw new Error(`R2 upload failed: ${putRes.status} ${putRes.statusText}`);
  }
  onProgress?.("upload", 100);

  // 3. Finalise — archives prior master, sets new one
  onProgress?.("save", 0);
  const finRes = await apiFetch("/deliverables/finalise-master", {
    method: "POST",
    body: JSON.stringify({
      line_id:         lineId,
      object_key,
      file_name:       file.name,
      file_size_bytes: file.size,
      mime_type:       finalMime,
      version_label:   args.versionLabel ?? null,
      archive_notes:   args.archiveNotes ?? null,
    }),
  });
  if (!finRes.ok) {
    const err = await finRes.json().catch(() => ({}));
    throw new Error(`finalise-master failed (${finRes.status}): ${err.error ?? "unknown"}${err.detail ? ` — ${err.detail}` : ""}`);
  }
  const { line_id, object_key: returnedKey } = await finRes.json();
  onProgress?.("save", 100);

  return { line_id, object_key: returnedKey };
}

// -----------------------------------------------------------------------------
export async function getDeliverableSignedUrl(
  storage_path: string,
  contentDisposition?: string,
): Promise<string | null> {
  try {
    const res = await apiFetch("/deliverables/sign-download", {
      method: "POST",
      body: JSON.stringify({
        storage_path,
        content_disposition: contentDisposition,
      }),
    });
    if (!res.ok) return null;
    const { download_url } = await res.json();
    return download_url ?? null;
  } catch {
    return null;
  }
}

// -----------------------------------------------------------------------------
export async function deleteDeliverableUpload(upload_id: string): Promise<void> {
  const res = await apiFetch(`/deliverables/upload/${upload_id}`, {
    method: "DELETE",
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(`delete failed (${res.status}): ${err.error ?? "unknown"}`);
  }
}
